import { InfoPopover, InfoPopoverProps, Stack } from 'component-library';
import { forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianViewBox } from 'recharts/types/util/types';
import { LabelProps } from 'recharts-overriden';
import { ImplicitLabelType } from 'recharts-overriden/types/component/Label';
import { useResizeObserver } from 'usehooks-ts';

import { ConservationBenchmarkType, TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { DEFAULT_DOT_RADIUS, Dot } from '@/components/Charts/components/Dot';
import { SHARED_CHART_COLORS } from '@/components/Charts/constants';
import { useScreenSize } from '@/hooks/useScreenSize';
import {
  DotProps,
  Line,
  ReferenceLine,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';

type BaseLabelProps = {
  label: string;
  infoPopoverProps?: InfoPopoverProps | null;
  x: number;
  y: number;
};

const BaseLabel = forwardRef<HTMLDivElement, BaseLabelProps>(({ infoPopoverProps, label, x, y }, ref) => {
  const isSmallScreen = useScreenSize() === 'small';

  return (
    <foreignObject x={x} y={y} width='100%' height='100%' style={{ overflow: 'visible' }}>
      <Stack
        direction='row'
        spacing={2}
        ref={ref}
        className='w-fit self-end rounded pl-1 bg-[rgba(255,_255,_255,_0.6)]'
      >
        <Stack direction='row' spacing={1} className='typography-caption whitespace-nowrap '>
          {label}
          {!isSmallScreen && infoPopoverProps && <InfoPopover {...infoPopoverProps} />}
        </Stack>
      </Stack>
    </foreignObject>
  );
});
BaseLabel.displayName = 'BaseLabel';

type TrendLabelProps = LabelProps & {
  trend: TrendLabelEnum;
  lineColor: string;
  strokeWidth: number;
  infoPopoverProps?: InfoPopoverProps | null;
  xOffset?: number;
};

export const TrendLabel = ({
  x,
  y,
  infoPopoverProps,
  trend,
  lineColor,
  strokeWidth,
  xOffset = 60,
}: TrendLabelProps) => {
  const { t } = useTranslation();

  const labelRef = useRef(null);
  const { height } = useResizeObserver({ ref: labelRef });

  const xPositionInitial = (Number(x) || 0) + DEFAULT_DOT_RADIUS / 2; // Usage of DEFAULT_DOT_RADIUS prevent the line from overlapping the dot
  const xPositionFinal = (Number(x) || 0) + xOffset;
  const yPosition = (Number(y) || 0) - (Number(height) || 0) / 2;

  return (
    <g>
      <line x1={xPositionInitial} x2={xPositionFinal} y1={y} y2={y} stroke={lineColor} strokeWidth={strokeWidth} />
      <BaseLabel
        label={t(`shared.projects.project.conservation.trendLabel.${trend}`)}
        infoPopoverProps={infoPopoverProps}
        x={xPositionFinal}
        y={yPosition}
        ref={labelRef}
      />
    </g>
  );
};

type BenchmarkReferenceLineProps = {
  value: number;
  benchmark: ConservationBenchmarkType;
  infoPopoverProps?: InfoPopoverProps | null;
};

export const RenderBenchmarkReferenceLine = ({ value, benchmark, infoPopoverProps }: BenchmarkReferenceLineProps) => {
  return (
    <ReferenceLine
      label={(props) => <BenchmarkLabel benchmark={benchmark} infoPopoverProps={infoPopoverProps} {...props} />}
      stroke={SHARED_CHART_COLORS.referenceLines.stroke}
      strokeDasharray='4 4'
      strokeWidth={2}
      y={value}
      ifOverflow='extendDomain'
    />
  );
};

type BenchmarkLabelProps = {
  benchmark: ConservationBenchmarkType;
  infoPopoverProps?: InfoPopoverProps | null;
  viewBox: CartesianViewBox;
};

const BenchmarkLabel = ({ benchmark, infoPopoverProps, viewBox }: BenchmarkLabelProps) => {
  const labelRef = useRef(null);
  const { t } = useTranslation();
  const { height } = useResizeObserver({ ref: labelRef });

  const { width, y } = viewBox;

  const xPosition = (Number(width) || 0) - 20;
  const yPosition = (Number(y) || 0) - (Number(height) || 0) - 5;

  return (
    <BaseLabel
      label={t(`shared.projects.project.conservation.benchmark.${benchmark}`)}
      infoPopoverProps={infoPopoverProps}
      x={xPosition}
      y={yPosition}
      ref={labelRef}
    />
  );
};

export type RenderTrendLineProps = {
  dataKey: string;
  stroke: string;
  label: ImplicitLabelType | undefined;
  dotFill: string;
};

export const RenderTrendLine = ({ dataKey, stroke, label, dotFill }: RenderTrendLineProps) => {
  return (
    <Line
      dataKey={dataKey}
      id={`${dataKey}-trend-line`}
      stroke={stroke}
      connectNulls
      isAnimationActive={false}
      strokeWidth={2}
      label={label}
      activeDot={(props: DotProps) => (
        <Dot.Container {...props}>
          <Dot.Active {...props} fill={dotFill} />
        </Dot.Container>
      )}
      dot={(props: DotProps) => (
        <Dot.Container {...props}>
          <Dot.Default {...props} fill={dotFill} />
        </Dot.Container>
      )}
    />
  );
};
